import { useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography } from '@altermaind/web/ui';
import { Dialog } from '@/_shared/components/Dialog';
import { useNotification } from '@/_shared/hooks';
import { deleteDelegateCall, getDelegatesCall } from '@/organization/state';

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
  userId: string;
};

export const RemoveDelegate = ({ isOpen, closeDialog, userId }: Props) => {
  const t = useTranslations('settings.tabs.delegates.removeDelegate.dialog');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { execute: executeDeleteDelegate, loading: loadingDeleteDelegate } =
    deleteDelegateCall.hook();
  const { execute: executeGetDelegates } = getDelegatesCall.hook();

  const onConfirm = useCallback(() => {
    executeDeleteDelegate({
      userId,
      successSaga: () => {
        notify(tShared('success.genericApiSuccess'), 'success');
        closeDialog();
        executeGetDelegates({
          start: 0,
          length: 1000,
        });
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [executeDeleteDelegate, userId, notify, tShared, closeDialog, executeGetDelegates]);

  return (
    <Dialog
      maxWidth='700px'
      name='removeDelegate'
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
    >
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>

      <Box mb='xs' mt='m' width='100%'>
        <Typography variant='p2/light' mb='xs' color='primary/text' textAlign='center'>
          {t('description')}
        </Typography>
        <Typography variant='p2/light' fontWeight='bold' color='primary/text' textAlign='center'>
          {t('confirmation')}
        </Typography>
        <Box mt='s' alignItems='center' display='flex' flexDirection='row' justifyContent='center'>
          <Button variant='link/primary' onClick={() => closeDialog()} mr='s' mt='s'>
            {t('buttons.cancel')}
          </Button>
          <Button
            loading={loadingDeleteDelegate}
            disabled={loadingDeleteDelegate}
            mt='s'
            data-testid='remove-edit-button-delegate'
            onClick={onConfirm}
          >
            {t('buttons.confirm')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
